import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChannelStory, SeeNumbersHint } from "./channels/ChannelStory";
import { friendlyRange } from "./compare/headlineTotals";
import { ChevronDown, Users, TrendingUp, MousePointer, Eye, Sparkles } from "lucide-react";

const TikTokIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.4a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.3 6.34 6.34 0 009.49 21.6a6.34 6.34 0 006.34-6.34V8.7a8.16 8.16 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.13z"/>
  </svg>
);

// Consistent k/M formatter so "12,848" and "142,065" don't dominate headlines.
const fmt = (n) => {
  if (n == null) return "-";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return Math.round(n).toLocaleString();
};

export const TikTokSection = ({ data, clientName, reportStart, reportEnd, onViewCreative, story = null }) => {
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [showWeekly, setShowWeekly] = useState(false);
  const [showAudience, setShowAudience] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const overview = data?.has_data ? data.overview : null;
  const social = data?.has_data ? data.social : null;
  const campaigns = data?.has_data ? data.campaigns : null;
  const demographics_by_age = data?.has_data ? data.demographics_by_age : [];
  const demographics_by_gender = data?.has_data ? data.demographics_by_gender : [];
  const weekly_trends = data?.has_data ? data.weekly_trends : [];

  const maxWeeklyImp = useMemo(() => Math.max(...(weekly_trends || []).map(w => w.impressions), 1), [weekly_trends]);
  const maxAgeImp = useMemo(() => Math.max(...(demographics_by_age || []).map(d => d.impressions), 1), [demographics_by_age]);
  const topAgeGroup = useMemo(() => (demographics_by_age || []).reduce((best, d) => d.impressions > (best?.impressions || 0) ? d : best, null), [demographics_by_age]);

  if (!data || !data.has_data) return null;

  // --- Retention story: the 4 donuts ARE the headline visual for TikTok. ---
  const retentionSteps = [
    { label: "25%", value: overview.video_views_25, color: "#00f2ea", benchmark: 25 },
    { label: "50%", value: overview.video_views_50, color: "#10b981", benchmark: 12 },
    { label: "75%", value: overview.video_views_75, color: "#8b5cf6", benchmark: 7 },
    { label: "100%", value: overview.video_views_100, color: "#ff0050", benchmark: 4 },
  ].map(s => ({
    ...s,
    pct: overview.video_views_2s > 0 ? Math.round((s.value / overview.video_views_2s) * 100) : 0,
  }));

  const completionPct = overview.video_views_2s > 0
    ? Math.round((overview.video_views_100 / overview.video_views_2s) * 100)
    : 0;

  return (
    <div className="relative z-10 mb-8" data-testid="tiktok-section">
      <div className="bg-gradient-to-br from-slate-500/10 to-zinc-500/10 rounded-2xl p-6 border border-slate-500/20">

        {/* Header (clickable toggle) */}
        <div
          onClick={() => setIsOpen(v => !v)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsOpen(v => !v); } }}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          data-testid="tiktok-toggle"
          className="flex items-center gap-4 cursor-pointer select-none group"
        >
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00f2ea] to-[#ff0050] flex items-center justify-center flex-shrink-0">
            <TikTokIcon />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[#00f2ea] text-xs font-semibold uppercase tracking-wider mb-0.5">{clientName}{/s$/i.test(clientName || "") ? "'" : "'s"}</p>
            <h3 className="text-2xl font-bold text-white">TikTok Campaign Performance</h3>
            {reportStart && reportEnd && (
              <p className="text-slate-400 text-xs mt-0.5">{friendlyRange(reportStart, reportEnd)}</p>
            )}
          </div>
          <ChevronDown className={`w-5 h-5 text-[#00f2ea] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </div>

        <div onClick={() => setIsOpen(v => !v)} className="cursor-pointer select-none group" data-testid="tiktok-story-area">
          <ChannelStory story={story} testId="tiktok-section-story" />
          <SeeNumbersHint open={isOpen} className="text-[#00f2ea]" />
        </div>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="tiktok-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="pt-8 space-y-8">

                {/* ====== ACT 1 — THE HEADLINE ====== */}
                {/* One sentence, not a stat grid. */}
                <div data-testid="tiktok-headline">
                  <p className="text-[#00f2ea] text-[11px] font-bold uppercase tracking-[0.25em] mb-3">Reach</p>
                  <p className="text-white text-2xl md:text-3xl font-bold leading-tight">
                    Your videos earned{" "}
                    <span className="text-[#00f2ea]">{fmt(overview.impressions)}</span> impressions,
                    reaching{" "}
                    <span className="text-white">{fmt(overview.reach)}</span> unique viewers.
                  </p>
                  {/* Subtitle: one signal — CTR with benchmark context. */}
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-slate-400 text-xs">
                    <span>
                      <span className={overview.ctr >= 0.3 ? "text-emerald-400 font-semibold" : "text-amber-400 font-semibold"}>
                        {overview.ctr}% CTR
                      </span>
                      <span className="text-slate-600"> (TikTok avg 0.3–0.5%)</span>
                    </span>
                  </div>
                </div>

                {/* ====== ACT 2 — THE RETENTION STORY (the star) ====== */}
                <div className="bg-white/[0.04] rounded-2xl p-5 md:p-6 border border-white/10" data-testid="tiktok-retention">
                  <div className="flex items-start gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl bg-[#00f2ea]/15 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-[#00f2ea]" />
                    </div>
                    <div>
                      <p className="text-[#00f2ea] text-[11px] font-bold uppercase tracking-[0.25em] mb-1">Retention</p>
                      <p className="text-white text-lg md:text-xl font-bold leading-snug">
                        How many kept watching?
                      </p>
                      <p className="text-slate-400 text-xs md:text-sm mt-1">
                        Of the <span className="text-white font-semibold">{fmt(overview.video_views_2s)}</span> people
                        who watched past 2 seconds{overview.avg_play_time > 0 && (<>, the average watch time was <span className="text-white font-semibold">{overview.avg_play_time}s</span></>)}.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                    {retentionSteps.map(m => {
                      const radius = 36;
                      const benchRadius = 40;
                      const circumference = 2 * Math.PI * radius;
                      const benchCircumference = 2 * Math.PI * benchRadius;
                      const strokeDash = (m.pct / 100) * circumference;
                      const benchDash = (m.benchmark / 100) * benchCircumference;
                      const aboveBench = m.pct >= m.benchmark;
                      return (
                        <div key={m.label} className="flex flex-col items-center" data-testid={`tiktok-donut-${m.label}`}>
                          <div className="relative w-24 h-24">
                            <svg className="w-24 h-24 -rotate-90" viewBox="0 0 88 88">
                              <circle cx="44" cy="44" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="7" />
                              <circle
                                cx="44" cy="44" r={benchRadius}
                                fill="none"
                                stroke="rgba(255,255,255,0.18)"
                                strokeWidth="2"
                                strokeDasharray={`${benchDash} ${benchCircumference}`}
                              />
                              <circle
                                cx="44" cy="44" r={radius}
                                fill="none"
                                stroke={m.color}
                                strokeWidth="7"
                                strokeLinecap="round"
                                strokeDasharray={`${strokeDash} ${circumference}`}
                                style={{ transition: 'stroke-dasharray 0.8s ease' }}
                              />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <span className="text-white font-black text-xl leading-none tabular-nums">{m.pct}%</span>
                            </div>
                          </div>
                          <p className="text-white font-semibold text-xs mt-2">Watched {m.label}</p>
                          <p className="text-slate-500 text-[11px]">{fmt(m.value)} viewers</p>
                          <p className={`text-[11px] mt-0.5 font-medium ${aboveBench ? 'text-emerald-400' : 'text-slate-500'}`}>
                            {aboveBench ? `↑ Above ${m.benchmark}% avg` : `Avg ${m.benchmark}%`}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {completionPct > 0 && (
                    <p className="text-slate-400 text-xs md:text-sm mt-5 leading-relaxed">
                      <span className="text-white font-semibold">{fmt(overview.video_views_100)} people</span> watched the entire video: {" "}
                      {completionPct >= 5 ? (
                        <span className="text-emerald-400">a strong {completionPct}% completion rate that beats TikTok's 3–6% benchmark.</span>
                      ) : completionPct >= 3 ? (
                        <span className="text-emerald-400">a {completionPct}% completion rate, right on target with TikTok's 3–6% benchmark.</span>
                      ) : (
                        <span className="text-slate-300">a {completionPct}% completion rate. TikTok's benchmark is 3–6%; shorter, punchier intros can help boost this.</span>
                      )}
                    </p>
                  )}
                </div>

                {/* ====== ACT 3 — THE RESPONSE ====== */}
                <div className="bg-white/[0.04] rounded-2xl p-5 md:p-6 border border-white/10" data-testid="tiktok-response">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                      <MousePointer className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-emerald-400 text-[11px] font-bold uppercase tracking-[0.25em] mb-1">Response</p>
                      <p className="text-white text-lg md:text-xl font-bold leading-snug">
                        What did they do next?
                      </p>
                    </div>
                  </div>

                  {/* Single-row facts. No duplicate tiles. */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
                    <ResponseFact label="Clicked to learn more" value={overview.clicks} accent="text-emerald-400" />
                    {social?.likes > 0 && <ResponseFact label="Liked" value={social.likes} accent="text-pink-400" />}
                    {social?.shares > 0 && <ResponseFact label="Shared" value={social.shares} accent="text-sky-400" />}
                    {social?.follows > 0 && <ResponseFact label="Followed" value={social.follows} accent="text-violet-400" />}
                    {social?.profile_visits > 0 && <ResponseFact label="Visited profile" value={social.profile_visits} accent="text-[#00f2ea]" />}
                  </div>

                  {social?.total_engagement > 0 && (
                    <p className="text-slate-400 text-xs md:text-sm mt-5 leading-relaxed">
                      That's <span className="text-white font-semibold">{fmt(social.total_engagement)} total social interactions</span>: every one a person now connected to {clientName}'s brand.
                    </p>
                  )}
                </div>

                {/* ====== DISCLOSURE 1 — Campaign Breakdown ====== */}
                {campaigns?.length > 0 && (
                  <Disclosure
                    open={showCampaigns}
                    onToggle={() => setShowCampaigns(v => !v)}
                    icon={<TrendingUp className="w-4 h-4 text-[#00f2ea]" />}
                    title={`Ad breakdown (${campaigns.length})`}
                    subtitle="Performance comparison across ads"
                    testId="tiktok-campaigns-toggle"
                  >
                    <div className="space-y-2">
                      {campaigns.map((c, idx) => {
                        const bestCtr = campaigns.reduce((b, x) => x.ctr > (b?.ctr || 0) ? x : b, null);
                        const isBest = c.campaign_name === bestCtr?.campaign_name && campaigns.length > 1;
                        return (
                          <div
                            key={c.campaign_name}
                            className={`rounded-xl border ${isBest ? 'border-emerald-400/30 bg-emerald-500/[0.04]' : 'border-white/10 bg-white/[0.02]'}`}
                            data-testid={`tiktok-campaign-${idx}`}
                          >
                            <button
                              onClick={() => setExpandedCampaign(expandedCampaign === idx ? null : idx)}
                              className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.03] transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-white font-medium text-sm truncate">{c.campaign_name || "(Unnamed)"}</p>
                                  {isBest && (
                                    <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5 rounded">Top</span>
                                  )}
                                </div>
                                <p className="text-slate-500 text-xs mt-0.5">
                                  {fmt(c.impressions)} impressions · {fmt(c.reach)} reached
                                </p>
                              </div>
                              <div className="flex items-center gap-4 flex-shrink-0">
                                <div className="text-right">
                                  <p className={`text-sm font-bold tabular-nums ${c.ctr >= 0.3 ? 'text-emerald-400' : 'text-amber-400'}`}>{c.ctr}%</p>
                                  <p className="text-slate-600 text-[9px] uppercase tracking-wider">CTR</p>
                                </div>
                                <button
                                  onClick={(e) => { e.stopPropagation(); onViewCreative && onViewCreative(c.campaign_name); }}
                                  className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-400/10 px-2.5 py-1 text-[11px] font-semibold text-violet-200 hover:bg-violet-400/25 hover:text-white transition-colors"
                                  title="See the ad"
                                  data-testid={`tiktok-creative-${idx}`}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  See ad
                                </button>
                                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${expandedCampaign === idx ? 'rotate-180' : ''}`} />
                              </div>
                            </button>
                            {expandedCampaign === idx && (
                              <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm border-t border-white/5 pt-3">
                                <CampaignStat label="Video views" value={fmt(c.video_views)} />
                                <CampaignStat label="Avg play time" value={`${c.avg_play_time}s`} />
                                <CampaignStat label="Avg frequency" value={`${c.avg_frequency}x`} />
                                {(c.likes + c.shares + c.follows + c.profile_visits) > 0 && (
                                  <CampaignStat
                                    label="Social"
                                    value={`${c.likes}♥ · ${c.shares}↗ · ${c.follows}+ · ${c.profile_visits}👁`}
                                    span
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Disclosure>
                )}

                {/* ====== DISCLOSURE 2 — Audience ====== */}
                {demographics_by_age?.length > 0 && (
                  <Disclosure
                    open={showAudience}
                    onToggle={() => setShowAudience(v => !v)}
                    icon={<Users className="w-4 h-4 text-[#00f2ea]" />}
                    title="Who watched"
                    subtitle={topAgeGroup ? `Strongest audience: ${topAgeGroup.age} (${topAgeGroup.pct}% of impressions)` : "Age & gender breakdown"}
                    testId="tiktok-audience-toggle"
                  >
                    <p className="text-slate-400 text-[11px] uppercase tracking-[0.2em] mb-3">By age</p>
                    <div className="space-y-2 mb-5">
                      {demographics_by_age.filter(d => d.age !== 'Unknown').map(d => {
                        const barWidth = (d.impressions / maxAgeImp) * 100;
                        const isTop = d.age === topAgeGroup?.age;
                        return (
                          <div key={d.age} className="flex items-center gap-3" data-testid={`demo-age-${d.age}`}>
                            <span className="text-slate-300 text-xs font-medium w-10 text-right flex-shrink-0 tabular-nums">{d.age}</span>
                            <div className="flex-1 relative h-7 bg-white/5 rounded-md overflow-hidden">
                              <div
                                className={`h-full rounded-md transition-all ${isTop ? 'bg-gradient-to-r from-[#00f2ea] to-[#ff0050]' : 'bg-gradient-to-r from-[#00f2ea]/50 to-[#00f2ea]/20'}`}
                                style={{ width: `${Math.max(barWidth, 3)}%` }}
                              />
                              <span className="absolute inset-y-0 left-3 flex items-center text-white text-[11px] font-semibold tabular-nums">
                                {d.pct}%
                              </span>
                            </div>
                            <span className="text-slate-500 text-[11px] w-24 text-right tabular-nums flex-shrink-0">
                              {fmt(d.impressions)} imp
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {demographics_by_gender?.length > 0 && (
                      <>
                        <p className="text-slate-400 text-[11px] uppercase tracking-[0.2em] mb-3">By gender</p>
                        <div className="flex gap-3">
                          {demographics_by_gender.map(g => (
                            <div key={g.gender} className="flex-1 bg-white/5 rounded-lg p-3 text-center border border-white/5">
                              <p className="text-white font-bold text-xl tabular-nums">{g.pct}%</p>
                              <p className="text-slate-400 text-xs">{g.gender}</p>
                              <p className="text-slate-500 text-[11px] mt-0.5">{fmt(g.reach)} reached</p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </Disclosure>
                )}

                {/* ====== DISCLOSURE 3 — Weekly trends ====== */}
                {weekly_trends?.length > 1 && (
                  <Disclosure
                    open={showWeekly}
                    onToggle={() => setShowWeekly(v => !v)}
                    icon={<TrendingUp className="w-4 h-4 text-[#00f2ea]" />}
                    title="Week-over-week trend"
                    subtitle="Impressions by week"
                    testId="tiktok-weekly-toggle"
                  >
                    <div className="space-y-2">
                      {weekly_trends.map(w => {
                        const barWidth = (w.impressions / maxWeeklyImp) * 100;
                        const weekLabel = w.week.replace(/\s*~\s*/, ' – ').replace(/2026-/g, '');
                        return (
                          <div key={w.week} className="flex items-center gap-3">
                            <span className="text-slate-400 text-[11px] w-24 text-right flex-shrink-0 font-mono">{weekLabel}</span>
                            <div className="flex-1 relative h-7 bg-white/5 rounded-md overflow-hidden">
                              <div
                                className="h-full rounded-md bg-gradient-to-r from-[#00f2ea]/60 to-[#00f2ea]/20 transition-all"
                                style={{ width: `${Math.max(barWidth, 3)}%` }}
                              />
                              <span className="absolute inset-y-0 left-3 flex items-center text-white text-[11px] font-semibold tabular-nums">
                                {fmt(w.impressions)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-4 text-slate-500 text-[11px]">
                      <span>Total impressions: <span className="text-slate-300 font-semibold">{fmt(weekly_trends.reduce((s, w) => s + w.impressions, 0))}</span></span>
                    </div>
                  </Disclosure>
                )}

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// --- Shared sub-components ---

const ResponseFact = ({ label, value, accent }) => (
  <div>
    <p className={`${accent} text-2xl md:text-3xl font-black tabular-nums leading-none`}>{fmt(value)}</p>
    <p className="text-slate-500 text-[11px] mt-1.5 leading-snug">{label}</p>
  </div>
);

const CampaignStat = ({ label, value, span = false }) => (
  <div className={span ? "col-span-2 sm:col-span-4" : ""}>
    <p className="text-slate-500 text-[11px] uppercase tracking-wider">{label}</p>
    <p className="text-white text-sm font-semibold tabular-nums">{value}</p>
  </div>
);

const Disclosure = ({ open, onToggle, icon, title, subtitle, children, testId }) => (
  <div className="bg-white/[0.03] rounded-xl border border-white/10 overflow-hidden" data-testid={testId}>
    <button
      onClick={onToggle}
      aria-expanded={open}
      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
    >
      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-semibold">{title}</p>
        {subtitle && <p className="text-slate-500 text-[11px] truncate">{subtitle}</p>}
      </div>
      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key={`${testId}-body`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-4 pt-1">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);
